import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion as Motion, AnimatePresence } from "motion/react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { configSecondaryInfo } from "@/features/evento/eventoThunks";
import GenericMap from "@/assets/Mapsicle Map.webp";

export default function EventLocation({
  initialData = {},
  onSave,
  eventIsPublished,
}) {
  const dispatch = useDispatch();
  const { idEvento } = useParams();
  const { isLoading: eventoLoading } = useSelector((state) => state.evento);

  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState({
    direccion: initialData.direccion || "",
    ubicacion: initialData.ubicacion || "",
    // showMapButton: initialData.showMapButton !== false,
  });
  const [previewData, setPreviewData] = useState({ ...formData });

  useEffect(() => {
    if (initialData) {
      const newData = {
        direccion: initialData.direccion || "",
        ubicacion: initialData.ubicacion || "",
        // showMapButton: initialData.showMapButton !== false,
      };
      setFormData(newData);
      setPreviewData(newData);
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (formData.direccion.trim() === "") {
      alert("La dirección del evento no puede estar vacía.");
      return;
    }
    if (!idEvento) {
      console.error("No se encontró el ID del evento");
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);

    try {
      const configData = {
        direccion: formData.direccion,
        ubicacion: formData.ubicacion,
      };

      console.log("Guardando ubicación del evento:", configData);

      await dispatch(
        configSecondaryInfo({
          uidEvento: idEvento,
          configData,
        })
      ).unwrap();

      setPreviewData({ ...formData });
      setIsEditMode(false);
      setSaveSuccess(true);

      if (onSave) {
        onSave(formData);
      }

      // Mostrar mensaje de éxito por unos segundos
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error al guardar ubicación:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({ ...previewData });
    setIsEditMode(false);
  };

  return (
    <div className="relative bg-aw-bluedark-95 rounded-2xl p-4 sm:p-6 flex flex-col items-center w-full">
      {/* Edit/Preview Toggle Button */}
      {isEditMode ? (
        <></>
      ) : !eventIsPublished ? (
        <Motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsEditMode(!isEditMode)}
          className="absolute top-4 right-4 z-50 px-4 py-2 rounded-full bg-aw-green/85 text-aw-bluedark font-medium hover:bg-aw-green transition duration-300 flex items-center gap-2"
        >
          <FontAwesomeIcon
            icon={isEditMode ? "fa-solid fa-eye" : "fa-solid fa-edit"}
          />
          {isEditMode ? "Vista Previa" : "Editar"}
        </Motion.button>
      ) : (
        <></>
      )}

      <AnimatePresence mode="wait">
        {isEditMode ? (
          <Motion.div
            key="edit-mode"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full space-y-6"
          >
            <div className="text-white font-bold text-xl text-center mb-4">
              Ubicación del Evento
            </div>

            {/* Map Image Preview */}
            <div className="relative w-full h-36 rounded-lg overflow-hidden">
              <div className="absolute w-full h-full bg-aw-bluedark/70 flex flex-col gap-y-2 items-center justify-center px-4 z-10">
                {formData.direccion ? (
                  <span className="text-sm text-white text-center">
                    <span className="font-medium block">
                      <FontAwesomeIcon
                        icon="fa-solid fa-map-marker-alt"
                        className="text-red-500"
                      />{" "}
                      <b>Ubicación:</b>{" "}
                    </span>
                    {formData.direccion}
                  </span>
                ) : (
                  <></>
                )}
                {/* Google Maps Button */}
                {formData.ubicacion && (
                  <Motion.a
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    href={formData.ubicacion}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-aw-bluedark text-white px-4 py-2 rounded-full border border-aw-green hover:bg-aw-green/85 hover:text-aw-bluedark text-sm duration-300 flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon="fa-solid fa-map-marker-alt" />
                    Google Maps
                  </Motion.a>
                )}
              </div>
              <img
                src={GenericMap}
                alt="Mapa de ubicación"
                className="w-full h-full object-cover rounded-lg"
              />
            </div>

            {/* direccion Input */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <FontAwesomeIcon
                  icon="fa-solid fa-map-marker-alt"
                  className="mr-2"
                />
                Dirección del Evento
              </label>
              <input
                type="text"
                value={formData.direccion}
                onChange={(e) => handleInputChange("direccion", e.target.value)}
                placeholder="Ej: Calle 123, Ciudad, País"
                className="w-full px-4 py-3 bg-aw-bluedark border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-aw-green transition duration-300"
              />
              <p className="text-xs text-gray-400 mt-1">
                Ingresa la dirección completa del evento
              </p>
            </div>

            {/* Google Maps URL Input */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <FontAwesomeIcon icon="fa-solid fa-link" className="mr-2" />
                Enlace de Google Maps
              </label>
              <input
                type="url"
                value={formData.ubicacion}
                onChange={(e) => handleInputChange("ubicacion", e.target.value)}
                placeholder="https://maps.app.goo.gl/..."
                className="w-full px-4 py-3 bg-aw-bluedark border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-aw-green transition duration-300"
              />
              <p className="text-xs text-gray-400 mt-1">
                URL opcional para abrir la ubicación en Google Maps
              </p>
            </div>

            {/* Show Map Button Toggle */}
            {/* <div className="flex items-center justify-between p-4 bg-aw-bluedark border border-gray-600 rounded-lg">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon
                  icon="fa-solid fa-map"
                  className="text-aw-green text-xl"
                />
                <div>
                  <p className="text-white font-medium">Mostrar Botón de Google Maps</p>
                  <p className="text-gray-400 text-sm">
                    Habilitar enlace a Google Maps en la vista previa
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.showMapButton}
                  onChange={(e) =>
                    handleInputChange("showMapButton", e.target.checked)
                  }
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-aw-green/85"></div>
              </label>
            </div> */}

            {/* Save and Cancel Buttons */}
            <div className="flex gap-3 pt-4">
              <Motion.button
                whileHover={{ scale: isSaving ? 1 : 1.02 }}
                whileTap={{ scale: isSaving ? 1 : 0.98 }}
                onClick={handleSave}
                disabled={isSaving || eventoLoading}
                className={`flex-1 py-3 font-bold rounded-lg transition duration-300 flex items-center justify-center gap-2 ${
                  saveSuccess
                    ? "bg-green-600 text-white"
                    : isSaving || eventoLoading
                    ? "bg-gray-500 text-gray-300 cursor-not-allowed"
                    : "bg-aw-green/85 text-aw-bluedark hover:bg-aw-green"
                }`}
              >
                {isSaving || eventoLoading ? (
                  <>
                    <FontAwesomeIcon
                      icon="fa-solid fa-spinner"
                      className="animate-spin"
                    />
                    Guardando...
                  </>
                ) : saveSuccess ? (
                  <>
                    <FontAwesomeIcon icon="fa-solid fa-check" />
                    ¡Guardado!
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon="fa-solid fa-save" />
                    Guardar Ubicación
                  </>
                )}
              </Motion.button>
              <Motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCancel}
                disabled={isSaving || eventoLoading}
                className={`flex-1 py-3 font-bold rounded-lg transition duration-300 flex items-center justify-center gap-2 ${
                  isSaving || eventoLoading
                    ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                    : "bg-gray-600 text-white hover:bg-gray-500"
                }`}
              >
                <FontAwesomeIcon icon="fa-solid fa-times" />
                Cancelar
              </Motion.button>
            </div>
          </Motion.div>
        ) : (
          <Motion.div
            key="preview-mode"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            <div className="text-white font-bold text-xl text-center mb-4">
              Ubicación del Evento
            </div>
            {previewData.direccion ? (
              <div className="relative w-full h-36 mb-4">
                <div className="absolute w-full h-full bg-aw-bluedark/70 flex flex-col gap-y-2 items-center justify-center px-4 z-10">
                  <span className="text-sm text-white text-center">
                    <span className="font-medium block">
                      <FontAwesomeIcon
                        icon="fa-solid fa-map-marker-alt"
                        className="text-red-500"
                      />{" "}
                      <b>Ubicación:</b>{" "}
                    </span>
                    {previewData.direccion}
                  </span>
                  {/* Google Maps Button */}
                  {previewData.ubicacion && (
                    <Motion.a
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      href={previewData.ubicacion}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-aw-bluedark text-white px-4 py-2 rounded-full border border-aw-green hover:bg-aw-green/85 hover:text-aw-bluedark text-sm duration-300 flex items-center gap-2"
                    >
                      <FontAwesomeIcon icon="fa-solid fa-map-marker-alt" />
                      Google Maps
                    </Motion.a>
                  )}
                </div>
                <img
                  src={GenericMap}
                  alt="Mapa de ubicación"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <FontAwesomeIcon
                  icon="fa-solid fa-map-marked-alt"
                  className="text-5xl mb-3"
                />
                <p>No hay ubicación configurada</p>
                <p className="text-sm mt-1">
                  Haz clic en "Editar" para agregar
                </p>
              </div>
            )}
          </Motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
